import { AccountQueryKeys } from '../../queryKeys';
import { fetchAccountByCoin } from './service';
import { UseAccountByCoinProps, UseAccountByCoinQueryKey } from './props';
import { useEffect } from 'react';
import { Account, GenericError } from '@starsoft/common/models';
import { AxiosError, AxiosInstance } from 'axios';
import { AccountByCoinInputDto } from '@starsoft/common/dto';
import { useAxiosInstance } from '@starsoft/common/hooks';
import { useQuery } from '@tanstack/react-query';

/**
 * Custom hook to fetch account details by coin ID.
 *
 * @param {UseAccountByCoinProps} options - Configuration options for the query.
 * @param {string} options.coinId - The ID of the coin to fetch account details for.
 * @param {Function} [options.customOnSuccess] - Optional callback function to execute on successful data fetch.
 * @param {boolean} [options.enabled=true] - Flag to enable or disable the query.
 * @param {boolean} [options.refetchOnMount=true] - Flag to refetch data when the component mounts.
 *
 * @returns {Object} - Returns an object containing the query state and functions.
 * @returns {boolean} return.isLoading - Indicates if the query is currently loading.
 * @returns {Account} return.data - The fetched account data.
 * @returns {AxiosError<GenericError>} return.error - Error object if the query fails.
 * @returns {Function} return.refetch - Function to manually refetch the data.
 * @returns {boolean} return.isRefetching - Indicates if the query is currently refetching.
 */
export function useAccountByCoin(options?: UseAccountByCoinProps) {
  const {
    coinId,
    customOnSuccess,
    enabled = true,
    refetchOnMount = true,
  } = options ?? {};

  // Get an Axios instance for making HTTP requests
  const axiosInstance: AxiosInstance = useAxiosInstance();

  // Define the query key for caching and refetching purposes
  const queryKey: UseAccountByCoinQueryKey = [
    AccountQueryKeys.ByCoin,
    { coinId } as AccountByCoinInputDto,
  ];

  // Use the useQuery hook to fetch account data
  const { isPending, data, error, refetch, fetchStatus, isRefetching } =
    useQuery<
      Account,
      AxiosError<GenericError>,
      Account,
      UseAccountByCoinQueryKey
    >({
      queryKey,
      queryFn: queryContext => fetchAccountByCoin(queryContext, axiosInstance),
      enabled: !!coinId && enabled,
      refetchOnWindowFocus: false,
      refetchOnMount,
      staleTime: 5 * 1000, // Data is considered fresh for 5 seconds
    });

  // Function to handle successful data fetch
  function onSuccess() {
    if (!data || !customOnSuccess) {
      return;
    }
    customOnSuccess(data);
  }

  // Use useEffect to trigger onSuccess when data changes
  //eslint-disable-next-line
  useEffect(onSuccess, [data]);

  return {
    isLoading: isPending && fetchStatus !== 'idle',
    data,
    error,
    refetch,
    isRefetching,
  };
}
